import { Controller } from "@hotwired/stimulus"
import { multiDeviceTypeStore } from "../stores/device_types/multi_device_type_store";
import { bundleStore } from "../stores/device_types/bundle_store";
import { getBundles } from "shared/device_types/bundles";
import { useStore } from "stimulus-store";
import { allBundlesStore } from "../stores/device_types/all_bundles_store";
import { ONLY_CURRENT_BUNDLE_VERSIONS } from "constants/bundles_constants";

// Connects to data-controller="walkthrough-bundle-palette"
//
// TURBO DRIVE NOTE: bundleStore is a module-level singleton that persists across
// Turbo navigations. If the user previously selected a bundle on another page,
// that value will still be set when this controller connects. The bundle palette
// clears bundleStore via unselectBundleAndRadioButtons() on user interaction, but
// consider clearing it in connect() if stale selections cause issues on load.
export default class extends Controller {

    static stores = [
        allBundlesStore,
        bundleStore,
        multiDeviceTypeStore
    ]

    static targets = [
        'bundleContent'
    ]

    connect() {
        // Initialize stimulus-store integration
        useStore(this);

        this.populateBundlePalette();
    }

    disconnect() {
        // Clean up when controller is disconnected
        // Remove any event listeners or cleanup if needed
    }

    populateBundlePalette() {
        // Clear existing content
        if (this.hasBundleContentTarget) {
            this.bundleContentTarget.innerHTML = '';
        }

        getBundles(ONLY_CURRENT_BUNDLE_VERSIONS).then((bundles) => {
            // Add all bundles to the allBundlesStore. This is used to pull
            // device type data when submitting the form
            this.setAllBundlesStoreValue(bundles);

            // Sort bundles alphabetically by name
            const sortedBundles = bundles.sort((a, b) => a.name.localeCompare(b.name));

            // Generate HTML for all bundles with proper spacing
            const bundlesHtml = this.generateBundleRadioButtons(sortedBundles);

            // Update the bundle content with the generated HTML
            if (this.hasBundleContentTarget) {
                this.bundleContentTarget.innerHTML = bundlesHtml;
            }
        }).catch((error) => {
            console.error('Error loading bundles:', error);
            this.showError('Failed to load bundles. Please refresh the page and try again.');
        });
    }

    /**
     * Generate HTML for bundle radio buttons with proper spacing
     * Uses same approach as device models for consistent styling
     */
    generateBundleRadioButtons(bundles) {
        if (!bundles || bundles.length === 0) {
            return '<p class="text-muted">No bundles available</p>';
        }

        return bundles.map(bundle => {
            const radioId = bundle.id;
            const bundleIcon = bundle.icon || '';

            return (` 
                <input class="btn-check" 
                       type="radio" 
                       name="bundles" 
                       id="${ radioId }" 
                       value="${ bundle.id }"
                       data-action="click->walkthrough-bundle-palette#bundleSelected"
                       data-bundle-id="${ bundle.id }"
                       data-bundle-name="${ this.escapeHtml(bundle.name) }"
                       data-bundle-icon="${ bundleIcon }"
                       data-type="bundle"
                       autocomplete="off" />
                <label class="btn btn-outline-secondary" for="${ radioId }">${ this.escapeHtml(bundle.name) }</label>
            `)
        }).join('');
    }

    /**
     * Escape HTML to prevent XSS
     */
    escapeHtml(text) {
        const div = document.createElement('div');
        div.textContent = text;
        return div.innerHTML;
    }

    showError(message) {
        if (this.hasBundleContentTarget) {
            const errorDiv = document.createElement('div');
            errorDiv.className = 'alert alert-danger';
            errorDiv.textContent = message;
            this.bundleContentTarget.appendChild(errorDiv);
        }
    }

    // Bundle selection handling
    bundleSelected(event) {
        const radioInput = event.currentTarget

        // Only proceed if the radio button is checked
        if (!radioInput.checked) {
            return
        }

        const bundleId = radioInput.dataset.bundleId;
        const bundleName = radioInput.dataset.bundleName;
        const bundleIcon = radioInput.dataset.bundleIcon;

        if (!bundleId) {
            console.error('Bundle ID not found in radio button data attributes');
            return
        }

        // Find the complete bundle data from allBundlesStore
        const allBundles = this.allBundlesStoreValue || [];
        const selectedBundle = allBundles.find(bundle => bundle.id === bundleId);

        if (!selectedBundle) {
            console.error('Bundle not found in allBundlesStore:', bundleId);
            this.showError('Bundle data not available. Please refresh the page and try again.');
            return;
        }

        // Validate bundle has models for device creation
        if (!selectedBundle.models || selectedBundle.models.length === 0) {
            console.error('Bundle has no models defined:', bundleId);
            this.showError('This bundle has no devices defined and cannot be created.');
            return;
        }

        // Update bundle store with complete bundle information for item creation
        this.setBundleStoreValue({
            id: bundleId,
            name: bundleName,
            icon: bundleIcon,
            models: selectedBundle.models // Include models for bundle creation
        });

        // Clear multi-device type store when bundle is selected to ensure mutual exclusivity
        this.clearMultiDeviceTypeStore();

        // Hide device-specific form fields for bundle creation
        this.showDeviceFormForBundles();
    }

    // Reactive store callbacks for mutual exclusivity
    onMultiDeviceTypeStoreUpdate(newValue) {
        // React to multi-device type selection by unselecting bundle radio buttons
        if (newValue && Array.isArray(newValue) && newValue.length > 0) {
            this.unselectBundleAndRadioButtons()
        }
    }

    // Bundle store management methods
    clearMultiDeviceTypeStore() {
        // Clear multi-device type store - manufacturerStore and modelStore will be
        // automatically cleared by walkthrough_device_palette_controller's onMultiDeviceTypeStoreUpdate
        this.setMultiDeviceTypeStoreValue([])
    }

    // Unselect any selected bundle and radio button
    unselectBundleAndRadioButtons() {
        // Clear the bundle store
        this.setBundleStoreValue({});

        let radioButtons = [];

        // First try to use the target if available
        if (this.hasBundleContentTarget) {
            radioButtons = this.bundleContentTarget.querySelectorAll('input[type="radio"][name="bundles"]');
        }

        // If no radio buttons found via target, search more broadly for bundle radio buttons
        if (radioButtons.length === 0) {
            // Search for bundle-specific radio buttons by name and data attribute
            radioButtons = document.querySelectorAll('input[type="radio"][name="bundles"][data-type="bundle"]');
        }

        // Unselect all found radio buttons
        radioButtons.forEach(radioButton => {
            if (radioButton.checked) {
                radioButton.checked = false;
            }
        });
    }

    // Clear bundle selection (public method for tab click)
    clearBundleSelection() {
        this.unselectBundleAndRadioButtons();
    }

    showDeviceFormForBundles() {
        // Clear out and hide the name, port id, dns name, IP address, and mac address fields
        const nameField = document.getElementById('walkthrough_name');
        const portIdField = document.getElementById('walkthrough_port_id');
        const dnsNameField = document.getElementById('walkthrough_dns_name');
        const ipAddressField = document.getElementById('walkthrough_ip_address');
        const macAddressField = document.getElementById('walkthrough_mac_address');

        // Clear and hide name field
        if (nameField) {
            nameField.value = '';
            const nameGroup = nameField.closest('.form-group, .mb-3, .field');
            if (nameGroup) {
                nameGroup.style.display = 'none';
            }
        }

        // Clear and hide port ID field
        if (portIdField) {
            portIdField.value = '';
            const portGroup = portIdField.closest('.form-group, .mb-3, .field');
            if (portGroup) {
                portGroup.style.display = 'none';
            }
        }

        // Clear and hide DNS name field
        if (dnsNameField) {
            dnsNameField.value = '';
            const dnsGroup = dnsNameField.closest('.form-group, .mb-3, .field');
            if (dnsGroup) {
                dnsGroup.style.display = 'none';
            }
        }

        // Clear and hide IP address field
        if (ipAddressField) {
            ipAddressField.value = '';
            const ipGroup = ipAddressField.closest('.form-group, .mb-3, .field');
            if (ipGroup) {
                ipGroup.style.display = 'none';
            }
        }

        // Clear and hide MAC address field
        if (macAddressField) {
            macAddressField.value = '';
            const macGroup = macAddressField.closest('.form-group, .mb-3, .field');
            if (macGroup) {
                macGroup.style.display = 'none';
            }
        }
    }
}